import { type FC, useCallback, type MouseEventHandler } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

import { getForcedCurrency } from 'dibs-currency-helpers/exports/getForcedCurrency.ts';
import { usePrerenderedCheckout } from 'dibs-checkout/exports/prerender/usePrerenderedCheckout.ts';
import { hidePage } from 'dibs-buyer-layout/exports/hidePage.ts';
import { useAddItemToCart } from 'dibs-buyer-layout/exports/useAddItemToCart.ts';
import { trackPurchaseClick, type Location } from '../../tracking/actionButtonsTracking.ts';

import { Button } from 'dibs-elements/exports/Button.tsx';
import { type PdpSharedBuyItNowButton_item$key } from './__generated__/PdpSharedBuyItNowButton_item.graphql.ts';
import { type PdpSharedBuyItNowButton_user$key } from './__generated__/PdpSharedBuyItNowButton_user.graphql.ts';
import { type PdpSharedBuyItNowButton_viewer$key } from './__generated__/PdpSharedBuyItNowButton_viewer.graphql.ts';

const userFragment = graphql`
    fragment PdpSharedBuyItNowButton_user on User {
        serviceId
    }
`;

const viewerFragment = graphql`
    fragment PdpSharedBuyItNowButton_viewer on Viewer {
        buyNowAddToCheckout: featureFlag(feature: "buyNowAddToCheckout")
    }
`;

const itemFragment = graphql`
    fragment PdpSharedBuyItNowButton_item on Item {
        hasPrivateListing
        ...usePrerenderedCheckout_item
        ...useAddItemToCart_item
        ...actionButtonsTracking_item
    }
`;

export const PdpSharedBuyItNowButton: FC<{
    item: PdpSharedBuyItNowButton_item$key;
    user: PdpSharedBuyItNowButton_user$key | null | undefined;
    viewer: PdpSharedBuyItNowButton_viewer$key | null | undefined;
    skuId?: string;
    quantitySelected?: string;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    location?: Location;
    className?: string;
}> = ({
    item: itemRef,
    user: userRef,
    viewer: viewerRef,
    skuId,
    quantitySelected,
    disabled,
    size = 'large',
    location,
    className,
}) => {
    const environment = useRelayEnvironment();
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const userId = user?.serviceId || null;
    const addToCheckout = !!viewer?.buyNowAddToCheckout;

    const { redirect: redirectToCheckout } = usePrerenderedCheckout({
        hideParentPage: hidePage,
        item,
        params: {
            skuId,
            quantity: quantitySelected,
            offerCurrency: getForcedCurrency(),
            triggerType: location === 'STICKY_HEADER' ? 'pdp-header-purchase' : undefined,
        },
    });
    const { addItemToCart } = useAddItemToCart({
        quantitySelected: quantitySelected || '1',
        item,
        userId,
        skuId: skuId || null,
    });

    const onClick: MouseEventHandler = useCallback(
        event => {
            trackPurchaseClick({
                item,
                event,
                location,
                environment,
                quantity: Number(quantitySelected),
            });
            if (!item?.hasPrivateListing && addToCheckout) {
                addItemToCart({ type: 'CHECKOUT' });
            }
            redirectToCheckout();
        },
        [
            item,
            location,
            environment,
            quantitySelected,
            addToCheckout,
            addItemToCart,
            redirectToCheckout,
        ]
    );

    return (
        <Button
            fullWidth
            type="primary"
            size={size}
            onClick={onClick}
            disabled={disabled}
            className={className}
            dataTn="pdp-shared-buy-it-now-button"
        >
            <FormattedMessage id="PdpSharedBuyItNowButton.buyItNow" defaultMessage="Buy now" />
        </Button>
    );
};
